import React, { useCallback, useEffect, useState } from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';
import { useSubscription } from 'react-apollo';

import { getWorkingDayEndDateTime, getWorkingDayStartDateTime } from '../../lib/time';
import Comps from './comps';
import { SubResponse } from '../../apollo/types';
import { useDeviceInfo } from '../../retail-config/device';
import { withSubDedup } from '../../apollo/with-sub-dedup';
import { DAILY_LIST_START_HOURS } from '../../lib/constants';
import { robotoFonts } from '../../components/typography';
import { StoreState } from '../../store/types';
import { usePlayerCardIdentifiers } from '../../components/player-card';
import { CompVoucher } from '.';
import { useSetIsDetailSidebarOpen } from '../../components/player-card/state';
import { PlayerCardOperator } from '../../types';
import playerCardOperators from '../providers';

export type SidebarView = 'sports' | 'comps' | 'racing';
type ChildType = {
  [key: string]: React.ReactNode;
};

export default function InfoWithComps({
  primaryIdentifier,
  children,
  sidebarTab,
  setSidebarTab,
  cardOperator
}: {
  primaryIdentifier: string;
  children: ChildType;
  sidebarTab: SidebarView;
  setSidebarTab: (sidebarTab: SidebarView) => void;
  cardOperator: PlayerCardOperator;
}) {
  const { data, loading } = useQuery<{ compVouchers: CompVoucher[] }>(compVouchersQuery, {
    context: { skipGlobalErrorHandling: true },
    variables: useCompVouchersQueryVariables()
  });

  const openSidebar = useSetIsDetailSidebarOpen();

  const [compVouchers, setCompVouchers] = useState<CompVoucher[]>([]);
  const assignedCompVouchers = compVouchers.filter(c => c.status === 'ASSIGNED');
  const {
    slotId: { venueId, type: deviceType, slotNumber },
    features: { comps, racingRewards }
  } = useDeviceInfo();

  const handleMenuClick = (sidebarTab: SidebarView) => () => !loading && setSidebarTab(sidebarTab);

  useSubscription<SubResponse>(compVoucherSubscription, {
    variables: { venueId, deviceType, slotNumber },
    onSubscriptionData: withSubDedup(({ subscriptionData: { data } }) => {
      const voucher = data?.sub?.payload as CompVoucher;
      if (voucher && !compVouchers.some(v => v.uuid === voucher.uuid)) {
        setCompVouchers([...compVouchers, voucher]);
        openSidebar(true);
        setSidebarTab('comps');
      }
    })
  });

  useEffect(() => {
    if (loading) {
      return;
    }
    const vouchers = data?.compVouchers;
    if (vouchers && vouchers.length > 0) {
      setCompVouchers(vouchers);
    }
  }, [data, loading]);

  const handleVoucherUpdated = useCallback((updatedVoucher: CompVoucher) => {
    setCompVouchers(vouchers => {
      const voucherIndex = vouchers.findIndex(v => v.uuid === updatedVoucher.uuid);
      if (voucherIndex !== -1) {
        const copy = [...vouchers];
        copy[voucherIndex] = updatedVoucher;
        return copy;
      }
      return vouchers;
    });
  }, []);

  const config = playerCardOperators[cardOperator];
  const showTabs =
    [
      config.showSportsPointsTab,
      racingRewards?.enabled && deviceType === 'POS' && config.showRacePointsTab,
      comps?.rewards?.enabled
    ].filter(Boolean).length > 1;

  return (
    <Wrapper>
      {showTabs && (
        <Menu>
          {config.showSportsPointsTab && (
            <MenuItem active={sidebarTab === 'sports'} onClick={handleMenuClick('sports')} data-testid="qa-sports-tab">
              Sports Points
            </MenuItem>
          )}
          {racingRewards?.enabled && deviceType === 'POS' && config.showRacePointsTab && (
            <MenuItem active={sidebarTab === 'racing'} onClick={handleMenuClick('racing')} data-testid="qa-race-tab">
              Race Points
            </MenuItem>
          )}
          {comps?.rewards?.enabled && (
            <MenuItem
              active={sidebarTab === 'comps'}
              data-count={assignedCompVouchers.length > 0 ? assignedCompVouchers.length : undefined}
              onClick={handleMenuClick('comps')}
              data-testid="qa-comps-tab">
              Comps
            </MenuItem>
          )}
        </Menu>
      )}
      {sidebarTab === 'sports' || sidebarTab === 'racing' ? (
        <>{children[sidebarTab]}</>
      ) : (
        <Comps vouchers={compVouchers} primaryIdentifier={primaryIdentifier} onVoucherUpdated={handleVoucherUpdated} />
      )}
    </Wrapper>
  );
}

const compVouchersQuery = gql`
  query compVouchers(
    $venueId: ID!
    $playerId: ID!
    $startDateTime: String!
    $endDateTime: String!
    $status: [String!]
  ) {
    compVouchers(
      where: {
        venueId: $venueId
        playerId: $playerId
        startDateTime: $startDateTime
        endDateTime: $endDateTime
        status: $status
      }
    ) {
      entitlement
      status
      uuid
      issuedAt
      redeemedAt
      source
    }
  }
`;

const compVoucherSubscription = gql`
  subscription ($venueId: String!, $deviceType: String!, $slotNumber: Int!) {
    sub: playerMessages(
      where: {
        schemaVersion: "1.0.0"
        venueId: $venueId
        deviceType: $deviceType
        slotNumber: $slotNumber
        messageTypes: ["COMP_VOUCHER_ASSIGNED"]
      }
    ) {
      metadata {
        message {
          topic
          offset
          type
        }
      }
      payload {
        ... on CompVoucherAssignmentPayload {
          uuid
          entitlement
          status
          source
        }
      }
    }
  }
`;

const useCompVouchersQueryVariables = () => ({
  playerId: usePlayerCardIdentifiers()?.playerIdentifierOrCardNumber,
  venueId: useSelector((state: StoreState) => state.device.info?.slotId.venueId),
  startDateTime: getWorkingDayStartDateTime(DAILY_LIST_START_HOURS),
  endDateTime: getWorkingDayEndDateTime(DAILY_LIST_START_HOURS),
  status: ['ASSIGNED', 'REDEEMED']
});

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 1.8rem;
  min-height: 0;
`;

const Menu = styled.ol`
  display: flex;
  margin: 0 -1.8rem 0;
  padding: 0;
`;

const MenuItem = styled.li<{ active: boolean }>`
  display: flex;
  font-size: ${robotoFonts.lg};
  justify-content: center;
  list-style: none;
  padding: 0 0 1rem 0;
  text-align: center;
  width: 50%;
  &[data-count]::after {
    border-radius: 50%;
    content: attr(data-count);
    display: inline-block;
    font-size: ${robotoFonts.md};
    font-weight: normal;
    margin: -0.8rem 0 0 0.5rem;
    height: 1.9rem;
    width: 1.9rem;
    ${({ theme }) => theme.components.rewards.detailsMenu.count}
  }
  ${({ active, theme }) =>
    active ? theme.components.rewards.detailsMenu.active : theme.components.rewards.detailsMenu.inactive}
`;

export const forTestsOnly = { compVouchersQuery, compVoucherSubscription };
